import { Component, ViewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { MenuService } from './services/heading-service';
import { SidebarService } from './services/side-bar.Service';
import { UserEntity } from './models/entities/user.entity';
import { UserService } from './services/api/user.service';
import { CardService } from './services/api/card.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { CalendarService } from './services/calendar.service';
import { trigger, transition, style, animate } from '@angular/animations';
import { environment } from 'src/environments/environment';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { SessionService } from './services/session.service';
import { ThemeService } from './services/theme.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [
        trigger('routeAnimations', [
            transition('* <=> *', [
                style({ opacity: 0 }),
                animate('0.3s ease-in', style({ opacity: 1 })),
            ]),
        ]),
    ],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('contentMain') contentMain!: ElementRef;

  title = 'phireboard';
  noPaddingMainContent: boolean = false;
  opened: boolean = false;
  loggedIn: boolean = false;
  isLogin: boolean = false;
  private userActivityTimeout: any;
  private updateAvailable = false;
  private versionSubscription: Subscription | null = null;
  private authStateSubscription: Subscription | null = null;

  constructor(
    private fireMessaging: AngularFireMessaging,
    private userService: UserService,
    private afMessaging: AngularFireMessaging,
    private menuService: MenuService,
    private sideBarService: SidebarService,
    private cardService: CardService,
    private router: Router,
    private CalendarService: CalendarService,
    private activeRoute: ActivatedRoute,
    private db: AngularFireDatabase,
    private snackBar: MatSnackBar,
    private afAuth: AngularFireAuth,
    private sessionService: SessionService,
    private themeService: ThemeService
  ) {
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.isLogin = event.url === '/';
      this.scrollToTop();
      this.ensureTenantInUrl();
    });

    menuService.noPaddingMainContent$.subscribe((noPadding) => {
      this.noPaddingMainContent = false;
    });

    sideBarService.sidebarOpen$.subscribe((opened: boolean) => {
      this.opened = opened;
    });

    this.userService.currentUser$.subscribe((user: UserEntity) => {
      if (user) {
        this.loggedIn = true;
       // this.requestPushNotificationsPermission(user);
        this.loadLeaderBoard();
        cardService.getPriorityItemsGlobal(user.key).subscribe(
          (cards) => {
            this.menuService.setPriorityItems(cards);
          }
        );
      } else {
        setTimeout(() => {
          this.loggedIn = false;
        }, 100);
      }
    });
  }

  ngOnInit() {
    this.setupUserActivityListener();
    this.subscribeToVersionUpdates();
    this.subscribeToAuthStateChanges();
  }
  
  ngOnDestroy() {
    // Clean up subscriptions
    if (this.versionSubscription) {
      this.versionSubscription.unsubscribe();
    }
    
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }
    
    // Clear any active timeouts
    if (this.userActivityTimeout) {
      clearTimeout(this.userActivityTimeout);
    }
  }
  
  // Keep ?t=<currentTenantId> in the address bar after every navigation so the
  // URL a user copies from the browser is portable across tenants — the auth
  // guard already honors ?t= to switch the recipient into the right tenant.
  private ensureTenantInUrl(): void {
    const path = window.location.pathname;
    // Skip auth/marketing pages; they aren't tenant-scoped.
    if (
      path === '/' ||
      path.startsWith('/login') ||
      path.startsWith('/register') ||
      path.startsWith('/accept-invite') ||
      path.startsWith('/forgot-password')
    ) {
      return;
    }
    const tenantId = this.userService.currentTenantId;
    if (!tenantId) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('t') === tenantId) return;
    url.searchParams.set('t', tenantId);
    // replaceState — don't push a history entry or trigger another navigation.
    window.history.replaceState(window.history.state, '', url.toString());
  }

  subscribeToVersionUpdates() {
    // Path to version information in Firebase
    const versionPath = 'app_versions/prod';
    
    // Subscribe to real-time updates from Firebase Database
    this.versionSubscription = this.db.object(versionPath).valueChanges().subscribe(
      (versionData: any) => {
        if (versionData && versionData.version) {
          // Compare version numbers properly
          const isNewer = this.isNewerVersion(versionData.version, environment.version);
          
          if (isNewer) {
            this.updateAvailable = true;
            
            // Configure snackbar to appear at the top of the screen
            const config: MatSnackBarConfig = {
              duration: 0, // No auto-dismiss
              verticalPosition: 'top',
              horizontalPosition: 'center',
              panelClass: ['update-notification-snackbar']
            };
            
            // Show notification to the user
            const snackBarRef = this.snackBar.open(
              `New version available! (${versionData.version})`, 
              'Refresh Now', 
              config
            );
            
            snackBarRef.onAction().subscribe(() => {
              this.refreshApp();
            });
            
            // Set up the inactivity timer
            this.resetUserActivityTimeout();
          }
        }
      },
      (error) => {
        // Check if it's a permission error
        if (this.isPermissionError(error)) {
          console.warn('Version update check failed due to permissions - user may need to re-authenticate');
          // Don't log this as an error since it's expected when session expires
          return;
        }
        
        console.error('Error checking for version updates:', error);
      }
    );
  }

  /**
   * Checks if the error is related to Firebase permissions
   */
  private isPermissionError(error: any): boolean {
    const errorMessage = error?.message || '';
    const errorCode = error?.code || '';
    
    return errorMessage.includes('Missing or insufficient permissions') ||
           errorMessage.includes('permission_denied') ||
           errorCode === 'permission-denied' ||
           errorMessage.includes('Client doesn\'t have permission to access the desired data');
  }

  /**
   * Compares two semantic version strings to determine if newVersion is newer than currentVersion
   * @param newVersion The new version string (e.g. "1.0.5")
   * @param currentVersion The current version string (e.g. "1.0.6")
   * @returns boolean True if newVersion is newer than currentVersion
   */
  isNewerVersion(newVersion: string, currentVersion: string): boolean {
    // Split version strings into components
    const newParts = newVersion.split('.').map(part => parseInt(part, 10));
    const currentParts = currentVersion.split('.').map(part => parseInt(part, 10));
    
    // Compare each component of the version number
    for (let i = 0; i < Math.max(newParts.length, currentParts.length); i++) {
      // If we've reached the end of newParts, it's not newer
      const newVal = i < newParts.length ? newParts[i] : 0;
      // If we've reached the end of currentParts, newVersion is newer
      const currentVal = i < currentParts.length ? currentParts[i] : 0;
      
      if (newVal > currentVal) {
        return true;
      } else if (newVal < currentVal) {
        return false;
      }
      // If equal, continue to next component
    }
    
    // If all components are equal, it's not a newer version
    return false;
  }

  setupUserActivityListener() {
    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    // Reset timer on user activity
    events.forEach(event => {
      document.addEventListener(event, () => {
        this.resetUserActivityTimeout();
      }, true);
    });
    
    // Initial setup
    this.resetUserActivityTimeout();
  }

  resetUserActivityTimeout() {
    // Clear existing timeout
    if (this.userActivityTimeout) {
      clearTimeout(this.userActivityTimeout);
    }
    
    // Only set up the timeout if an update is available
    if (this.updateAvailable) {
      // Set a new timeout for 60 seconds of inactivity
      this.userActivityTimeout = setTimeout(() => {
        this.refreshApp();
      }, 60000); // 60 seconds
    }
  }

  refreshApp() {
    window.location.reload();
  }

  scrollToTop() {
    if (this.contentMain) {
      this.contentMain.nativeElement.scrollTop = 0;
    }
  }

  loadLeaderBoard() {
    this.userService.getLeaderboard().subscribe((users) => {
      this.menuService.setLeaderboard(users);
    });
  }

  listenForMessages() {
    this.afMessaging.messages.subscribe((payload) => {
      

      // Extracting notification data
      const notificationTitle = (payload as any).notification.title;
      const notificationOptions = {
        body: (payload as any).notification.body,
        icon: (payload as any).data.icon,
        tag: (payload as any).data.tag,
        data: (payload as any).data // Pass data to handle click actions
      };

      // Displaying the notification
      const notification = new Notification(notificationTitle, notificationOptions);

      // Handling notification click
      notification.onclick = (event) => {
        event.preventDefault(); // Prevent the browser from focusing the Notification's tab
        if (notificationOptions.data && notificationOptions.data.url) {
          window.open(notificationOptions.data.url, '_blank');
        }
      };
    });
  }

  requestPushNotificationsPermission(user: UserEntity) {
    // requesting permission
    if (localStorage.getItem('permission') !== 'true') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.afMessaging.requestToken // getting tokens
            .subscribe(
              (token) => {
                // USER-REQUESTED-TOKEN
                
                user.pushtoken = token;
                this.userService.update(user);
                localStorage.setItem('permission', 'true');
              },
              (error) => {
                console.error(error);
              }
            );
        } else {
          console.error('Permission not granted for notifications');
        }
      });
    }
  }

  /**
   * Subscribe to Firebase Auth state changes
   */
  private subscribeToAuthStateChanges() {
    // Subscribe to auth state changes
    this.authStateSubscription = this.afAuth.authState.subscribe(async (user) => {
      if (user) {
        // User is logged in
        console.log('Auth state: user logged in');
      } else if (this.loggedIn) {
        // User was logged in but now isn't - session expired
        console.log('Auth state changed: user logged out');
        // The session service will handle the redirect
      }
    });
  }
}
